import { ReactNode } from 'react'
import { GlobalInboundRollbackResultItem } from '@/console/api/types'
import { DataTable, DataTableColumn } from '@/console/ui/DataTable'
import { Tag } from '@/console/ui/Tag'
import { MWMS_CANCEL_LABEL, OUTCOME_PRESENTATION, REASON_GUIDE, ROLLBACK_OUTCOME, countByOutcome, countByReason } from './globalRollback'
import styles from './GlobalRollback.module.css'

/** 판정 결과와 실행 결과가 함께 쓰는 열. 각 표는 앞이나 뒤에 자기 열(선택 · mWMS 취소 확인)만 덧붙인다. */
export const RESULT_COLUMNS: DataTableColumn[] = [
  { key: 'inboundDocumentNumber', label: '입고문서번호', mono: true },
  { key: 'cut', label: '컷' },
  { key: 'planCount', label: '입고지시', align: 'right' },
  { key: 'ledgerRevertCount', label: '원장 복원', align: 'right' },
  { key: 'mwmsCancel', label: 'mWMS 취소' },
  { key: 'outcome', label: '결과' },
  { key: 'reason', label: '사유 · 할 일' },
]

const cutOf = (item: GlobalInboundRollbackResultItem): string => (item.cutDate ? `${item.cutDate} · ${item.cutSeq ?? '-'}차` : '-')

const ReasonCell = ({ item }: { item: GlobalInboundRollbackResultItem }): JSX.Element => {
  const guide = item.reason ? REASON_GUIDE[item.reason] : null
  if (!guide && !item.detail) {
    return <>-</>
  }

  return (
    <div className={styles.reason}>
      {guide ? <span>{guide.meaning}</span> : null}
      {guide ? <span className={styles.action}>{guide.action}</span> : null}
      {item.detail ? <span className={styles.detail}>{item.detail}</span> : null}
    </div>
  )
}

export const resultCells = (item: GlobalInboundRollbackResultItem): Record<string, ReactNode> => {
  const outcome = OUTCOME_PRESENTATION[item.outcome]
  return {
    inboundDocumentNumber: item.inboundDocumentNumber,
    cut: cutOf(item),
    planCount: item.planCount ?? '-',
    ledgerRevertCount: item.ledgerRevertCount ?? '-',
    mwmsCancel: item.mwmsCancel ? MWMS_CANCEL_LABEL[item.mwmsCancel] : '-',
    outcome: <Tag tone={outcome.tone}>{outcome.label}</Tag>,
    reason: <ReasonCell item={item} />,
  }
}

/** 거부는 되돌릴 수 없다는 뜻이라 회색으로 가라앉힌다(§3.2). */
export const isMutedResult = (item: GlobalInboundRollbackResultItem): boolean => item.outcome === ROLLBACK_OUTCOME.REJECTED

export const OutcomeCounts = ({ results }: { results: GlobalInboundRollbackResultItem[] }): JSX.Element => (
  <div className={styles.counts}>
    {countByOutcome(results).map(({ outcome, count }) => (
      <Tag key={outcome} tone={OUTCOME_PRESENTATION[outcome].tone}>
        {OUTCOME_PRESENTATION[outcome].label} {count}건
      </Tag>
    ))}
  </div>
)

const REASON_SUMMARY_COLUMNS: DataTableColumn[] = [
  { key: 'meaning', label: '사유' },
  { key: 'action', label: '할 일' },
  { key: 'count', label: '건수', align: 'right' },
]

/** 사유가 붙은 문서가 없으면 그리지 않는다. */
export const ReasonSummary = ({ results }: { results: GlobalInboundRollbackResultItem[] }): JSX.Element | null => {
  const reasonCounts = countByReason(results)
  if (reasonCounts.length === 0) {
    return null
  }

  return (
    <DataTable
      columns={REASON_SUMMARY_COLUMNS}
      rows={reasonCounts.map(({ reason, count }) => ({
        key: reason,
        cells: { meaning: REASON_GUIDE[reason].meaning, action: REASON_GUIDE[reason].action, count: `${count}건` },
      }))}
    />
  )
}
